import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  AppShell,
  Navbar,
  Header,
  Text,
  useMantineTheme,
  Button,
  Image,
  Loader,
  ThemeIcon,
  UnstyledButton,
  Group,
  MediaQuery,
  Burger,
  Space,
  Divider,
  ScrollArea,
  MantineTheme,
  Popover,
  Stack,
  Avatar,
  ActionIcon,
  Tooltip,
  Badge,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

import { logout } from "../firebase";
import { deleteUser } from "../components/shared/hooks/user-management";
import { useFeatureFlags } from "../hooks/useFeatureFlags";

import "./app.css";
import { Collapse, Modal } from "@mantine/core";
import ChatBox from "../components/shared/chat-box";
import { RequestOnboardingHelpModal } from "../components/modals/request-onboarding-help-modal";
import {
  IconSparkles,
  IconAlertTriangle as AlertTriangle,
  IconArrowsTransferUp as ArrowsTransferUp,
  IconBook as Book,
  IconBuilding as Building,
  IconEye as Eye,
  IconHome as Home,
  IconMail as Mail,
  IconPageBreak as PageBreak,
  IconRecharging as Recharging,
  IconRoute as Route,
  IconTruck as Truck,
  IconUser as User,
  IconDots as Dots,
  IconGasStation as GasStation,
  IconArrowRightCircle as ArrowRightCircle,
  IconUsers as Users,
  IconBolt as Bolt,
  IconSelect as Select,
  IconClipboardData as ClipboardData,
  IconSettings as Settings,
  IconMessageCircle as MessageCircle,
  IconChevronDown as ChevronDown,
} from "@tabler/icons-react";

interface StyledLinkButtonProps {
  target: string;
  onClick?: () => void;
  selected?: boolean;
  children: React.ReactNode;
  disabled?: boolean;
  isAi?: boolean;
}

function StyledLinkButton({ target, onClick, selected, children, disabled, isAi }: StyledLinkButtonProps) {
  return (
    <UnstyledButton
      disabled={disabled}
      component={Link as any}
      to={target}
      onClick={onClick}
      sx={(theme) => ({
        display: "block",
        width: "100%",
        padding: theme.spacing.xs,
        borderRadius: theme.radius.sm,
        transition: "all 0.15s ease",
        background: isAi
          ? (selected ? "rgba(99, 49, 255, 0.06)" : "transparent")
          : "transparent",
        color:
          theme.colorScheme === "dark"
            ? theme.colors.dark[0]
            : theme.colors["cap-navy"],
        border: selected
          ? (isAi ? `1.5px solid ${theme.colors["cap-purple"]?.[4] || "purple"}` : "0.5px solid")
          : "0.5px solid transparent",
        "&:hover": {
          background: isAi
            ? "rgba(99, 49, 255, 0.08)"
            : (theme.colorScheme === "dark"
              ? theme.colors.dark[6]
              : theme.colors.gray[0]),
        },
      })}
    >
      {children}
    </UnstyledButton>
  );
}

interface MainLinkProps {
  icon: React.ReactNode;
  color: string;
  label: string;
  target?: string;
  selected?: boolean;
  clickHandler?: () => void;
  variant?: "filled" | "light" | "outline" | "default" | "subtle";
  disabled?: boolean;
  isAi?: boolean;
  badge?: React.ReactNode;
}

function MainLink({
  icon,
  color,
  label,
  target,
  selected,
  clickHandler,
  variant,
  disabled,
  isAi,
  badge,
}: MainLinkProps) {
  if (target) {
    return (
      <StyledLinkButton
        target={target}
        onClick={() => clickHandler && clickHandler()}
        selected={selected}
        disabled={disabled}
        isAi={isAi}
      >
        <Group position="apart" noWrap style={{ width: "100%" }}>
          <Group noWrap spacing="xs">
            <ThemeIcon
              color={color}
              variant={isAi ? "filled" : (variant as any)}
              style={isAi ? {
                background: "linear-gradient(135deg, #FF5D00 0%, #FFDE59 100%)",
                color: "white"
              } : undefined}
            >
              {icon}
            </ThemeIcon>
            <Text size="md">{label}</Text>
          </Group>
          {badge}
        </Group>
      </StyledLinkButton>
    );
  } else {
    return (
      <UnstyledButton
        onClick={() => clickHandler && clickHandler()}
        disabled={disabled}
        sx={(theme) => ({
          display: "block",
          width: "100%",
          padding: theme.spacing.xs,
          borderRadius: theme.radius.sm,
          transition: "all 0.15s ease",
          background: isAi
            ? (selected ? "rgba(99, 49, 255, 0.06)" : "transparent")
            : "transparent",
          color:
            theme.colorScheme === "dark"
              ? theme.colors.dark[0]
              : theme.colors["cap-navy"],
          border: selected
            ? (isAi ? `1.5px solid ${theme.colors["cap-purple"]?.[4] || "purple"}` : "0.5px solid")
            : "0.5px solid transparent",
          "&:hover": {
            background: isAi
              ? "rgba(99, 49, 255, 0.08)"
              : (theme.colorScheme === "dark"
                ? theme.colors.dark[6]
                : theme.colors.gray[0]),
          },
        })}
      >
        <Group position="apart" noWrap style={{ width: "100%" }}>
          <Group noWrap spacing="xs">
            <ThemeIcon
              color={color}
              variant={isAi ? "filled" : (variant as any)}
              style={isAi ? {
                background: "linear-gradient(135deg, #FF5D00 0%, #FFDE59 100%)",
                color: "white"
              } : undefined}
            >
              {icon}
            </ThemeIcon>
            <Text size="md">{label}</Text>
          </Group>
          {badge}
        </Group>
      </UnstyledButton>
    );
  }
}

const TemplateLinksData = [
  {
    icon: <GasStation />,
    label: "Roadside refuels",
    color: "red",
    variant: "filled" as const,
    target: "/dashboard/capturedRefuel",
  },
  {
    icon: <Eye />,
    label: "Tank readings",
    target: "/dashboard/capturedTankLevelReading",
    color: "cap-navy",
    variant: "filled" as const,
  },
  {
    icon: <ArrowsTransferUp />,
    label: "Fuel transfers (tanks)",
    target: "/dashboard/capturedFuelTankTransfer",
    color: "cap-blue",
    variant: "filled" as const,
  },
  {
    icon: <ArrowRightCircle />,
    label: "Onsite refuels",
    target: "/dashboard/capturedFuelTankIssue",
    color: "cap-purple",
    variant: "filled" as const,
  },
  {
    icon: <Recharging />,
    label: "Bulk deliveries",
    color: "yellow",
    target: "/dashboard/capturedBulkFuelReceived",
    variant: "filled" as const,
  }];

interface TemplateLinksProps {
  selectedPath: string;
  setBurger: (opened: boolean) => void;
}

export function TemplateLinks({ selectedPath, setBurger }: TemplateLinksProps) {
  const links = TemplateLinksData.map((link) => (
    <MainLink
      {...link}
      key={link.label}
      selected={selectedPath === link.target}
      clickHandler={() => setBurger(false)}
    />
  ));
  return (
    <div>
      <MainLink
        icon={<Settings color="gray" />}
        label="Configuration"
        target="/dashboard/captiva-fuel-configs"
        color="gray"
        variant="light"
        selected={selectedPath === "/dashboard/captiva-fuel-configs"}
        clickHandler={() => setBurger(false)}
      />
      <Divider />
      {links}
      <Divider />
      <MainLink
        icon={<Truck />}
        label="Assets"
        target="/dashboard/assets"
        selected={selectedPath === "/dashboard/assets"}
        color="cap-blue"
        variant="light"
        clickHandler={() => setBurger(false)}
      />
      <MainLink
        icon={<PageBreak />}
        label="Reports"
        color="cap-orange"
        target="/dashboard/reports"
        selected={selectedPath === "/dashboard/reports"}
        clickHandler={() => setBurger(false)}
      />
      <MainLink
        icon={<Book />}
        label="Advanced Reports"
        target="/dashboard/advanced-reports"
        color="cap-purple"
        selected={selectedPath === "/dashboard/advanced-reports"}
        clickHandler={() => setBurger(false)}
      />
      <MainLink
        icon={<Mail />}
        label="Alerts"
        target="/dashboard/notifications"
        selected={selectedPath === "/dashboard/notifications"}
        color="cap-blue"
        clickHandler={() => setBurger(false)}
      />
    </div>
  );
}

interface NavBarLinksProps {
  currentPath: string;
  setOpened: (opened: boolean) => void;
  theme: MantineTheme;
  companyId: string;
}

export function NavBarLinks({ currentPath, setOpened, theme, companyId }: NavBarLinksProps) {
  const { anomalyDetectionEnabled } = useFeatureFlags(companyId);
  const isFuelPath =
    currentPath.startsWith("/dashboard/captured") ||
    currentPath.startsWith("/dashboard/assets") ||
    currentPath.startsWith("/dashboard/reports") ||
    currentPath.startsWith("/dashboard/advanced-reports") ||
    currentPath.startsWith("/dashboard/notifications") ||
    currentPath.startsWith("/dashboard/captiva-fuel-configs");
  const isTripPath =
    currentPath.startsWith("/dashboard/trips") ||
    currentPath.startsWith("/dashboard/aggregated-trips-map");
  const isFlexPath =
    currentPath.startsWith("/dashboard/form-template") ||
    currentPath.startsWith("/dashboard/form-select-options") ||
    currentPath.startsWith("/dashboard/flex-reports") ||
    currentPath.startsWith("/dashboard/flex-notifications");

  const [fuelOpen, setFuelOpen] = useState<boolean>(isFuelPath);
  const [tripOpen, setTripOpen] = useState<boolean>(isTripPath);
  const [flexOpen, setFlexOpen] = useState<boolean>(isFlexPath);

  useEffect(() => {
    if (isFuelPath) setFuelOpen(true);
    if (isTripPath) setTripOpen(true);
    if (isFlexPath) setFlexOpen(true);
  }, [currentPath, isFuelPath, isTripPath, isFlexPath]);

  return (
    <ScrollArea {...({ scrollbars: "y" } as any)} style={{ height: "100%" }}>
      <Navbar.Section style={{ paddingLeft: theme.spacing.xs, paddingBottom: "12px" }}>
        <MainLink
          icon={<Home />}
          label="Overview"
          target="/dashboard/overview"
          color="cap-purple"
          variant="filled"
          selected={currentPath === "/dashboard/overview"}
          clickHandler={() => setOpened(false)}
        />
        <MainLink
          icon={<IconSparkles size={20} />}
          label="AI Assistant"
          target="/dashboard/ai-assistant"
          color="cap-purple"
          variant="filled"
          isAi={true}
          selected={currentPath === "/dashboard/ai-assistant"}
          clickHandler={() => setOpened(false)}
        />
        {anomalyDetectionEnabled && (
          <MainLink
            icon={<AlertTriangle size={20} />}
            label="Anomalies"
            target="/dashboard/anomalies"
            color="red"
            variant="light"
            selected={currentPath === "/dashboard/anomalies"}
            clickHandler={() => setOpened(false)}
          />
        )}
      </Navbar.Section>
      <Divider />

      <Navbar.Section>
        <Group
          align="center"
          noWrap
          style={{ padding: theme.spacing.xs, cursor: "pointer", display: "flex", justifyContent: "space-between" }}
          onClick={() => setFuelOpen((o) => !o)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
            <Image src="/captiva-fuel-icon.png" height={40} width={40} alt="Captiva Fuel" />
            <Text size="lg" weight={400}>
              Captiva Fuel
            </Text>
          </div>
          <ThemeIcon color="cap-purple" variant="light" size="sm">
            <ChevronDown color="#6331FF" strokeWidth={2.5} style={{ transform: fuelOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
          </ThemeIcon>
        </Group>
        <Collapse in={fuelOpen}>
          <div style={{ margin: "0 10px 10px" }}>
            <TemplateLinks
              selectedPath={currentPath}
              setBurger={setOpened}
            />
          </div>
        </Collapse>
      </Navbar.Section>
      <Divider />

      <Navbar.Section>
        <Group
          align="center"
          noWrap
          style={{ padding: theme.spacing.xs, cursor: "pointer", display: "flex", justifyContent: "space-between" }}
          onClick={() => setTripOpen((o) => !o)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
            <Image src="/captiva-trip-icon.png" height={40} width={40} alt="Captiva Trip" />
            <Text size="lg" weight={400}>
              Captiva Trip
            </Text>
          </div>
          <ThemeIcon color="cap-purple" variant="light" size="sm">
            <ChevronDown color="#6331FF" strokeWidth={2.5} style={{ transform: tripOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
          </ThemeIcon>
        </Group>
        <Collapse in={tripOpen}>
          <div style={{ margin: "0 10px 10px" }}>
            <MainLink
              icon={<Route />}
              label="Trips"
              target="/dashboard/trips"
              color="cap-navy"
              variant="filled"
              selected={currentPath === "/dashboard/trips"}
              clickHandler={() => setOpened(false)}
            />
          </div>
        </Collapse>
      </Navbar.Section>
      <Divider />

      <Navbar.Section>
        <Group
          align="center"
          noWrap
          style={{ padding: theme.spacing.xs, cursor: "pointer", display: "flex", justifyContent: "space-between" }}
          onClick={() => setFlexOpen((o) => !o)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
            <Image src="/captiva-flex.png" height={40} width={40} alt="Captiva Flex" />
            <Text size="lg" weight={400}>
              Captiva Flex
            </Text>
          </div>
          <ThemeIcon color="cap-purple" variant="light" size="sm">
            <ChevronDown color="#6331FF" strokeWidth={2.5} style={{ transform: flexOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
          </ThemeIcon>
        </Group>
        <Collapse in={flexOpen}>
          <div style={{ margin: "0 10px 10px" }}>
            <MainLink
              icon={<ClipboardData />}
              label="Form submissions"
              target="/dashboard/form-template-submissions"
              color="cap-blue"
              variant="filled"
              selected={currentPath === "/dashboard/form-template-submissions"}
              clickHandler={() => setOpened(false)}
            />
            <MainLink
              icon={<Book />}
              label="Form configurations"
              target="/dashboard/form-templates"
              color="cap-purple"
              variant="filled"
              selected={currentPath === "/dashboard/form-templates"}
              clickHandler={() => setOpened(false)}
            />
            <MainLink
              icon={<Select />}
              label="Option Sets"
              target="/dashboard/form-select-options"
              color="yellow"
              variant="filled"
              selected={currentPath === "/dashboard/form-select-options"}
              clickHandler={() => setOpened(false)}
            />
            <Divider />
            <MainLink
              icon={<PageBreak />}
              label="Reports"
              target="/dashboard/flex-reports"
              color="cap-orange"
              variant="filled"
              selected={currentPath === "/dashboard/flex-reports"}
              clickHandler={() => setOpened(false)}
            />
            <MainLink
              icon={<Mail />}
              label="Alerts"
              target="/dashboard/flex-notifications"
              color="cap-blue"
              variant="filled"
              selected={currentPath === "/dashboard/flex-notifications"}
              clickHandler={() => setOpened(false)}
            />
          </div>
        </Collapse>
      </Navbar.Section>
      <Divider />
      <Space h="sm" />
      <Navbar.Section style={{ paddingLeft: theme.spacing.xs }}>
        <Text size="lg" weight={400}>
          Settings
        </Text>
        <MainLink
          icon={<Building />}
          label="Company"
          target="/dashboard/settings"
          color="cap-blue"
          variant="light"
          selected={currentPath === "/dashboard/settings"}
          clickHandler={() => setOpened(false)}
        />
        <MainLink
          icon={<Users />}
          label="Users"
          target="/dashboard/users"
          color="cap-blue"
          variant="light"
          selected={currentPath === "/dashboard/users"}
          clickHandler={() => setOpened(false)}
        />
        <MainLink
          icon={<Bolt />}
          label="Actions"
          target="/dashboard/advanced-settings"
          color="cap-blue"
          variant="light"
          selected={currentPath === "/dashboard/advanced-settings"}
          clickHandler={() => setOpened(false)}
        />
      </Navbar.Section>
    </ScrollArea>
  );
}

interface ShellWithNavigationProps {
  user: any;
  children: React.ReactNode;
  companyId: string;
  auth?: any;
}

export function ShellWithNavigation({ user, children, companyId, auth }: ShellWithNavigationProps) {
  const theme = useMantineTheme();
  const isBelowLarge = useMediaQuery('(max-width: 1199px)');
  const [opened, setOpened] = useState<boolean>(false);
  const [aiModalOpen, setAiModalOpen] = useState<boolean>(false);
  const [popoverOpened, setPopoverOpened] = useState<boolean>(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);
  const [showDeleteAction, setShowDeleteAction] = useState<boolean>(false);
  const [helpModalOpened, setHelpModalOpened] = useState<boolean>(false);

  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  return (
    <AppShell
      styles={{
        main: {
          background: "white",
        },
      }}
      navbarOffsetBreakpoint="sm"
      asideOffsetBreakpoint="sm"
      fixed
      navbar={
        <Navbar
          p="md"
          hiddenBreakpoint="sm"
          hidden={!opened}
          width={{ sm: 300, lg: 300 }}
          style={{ color: theme.colors["cap-navy"] as any }}
        >
          <NavBarLinks currentPath={currentPath} setOpened={setOpened} theme={theme} companyId={companyId} />
        </Navbar>
      }
      header={
        <Header height={70} p="md">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <MediaQuery largerThan="sm" styles={{ display: "none" }}>
              <Burger
                opened={opened}
                onClick={() => setOpened((o) => !o)}
                size="md"
                color={theme.colors.gray[6]}
                mr="xl"
                aria-label={opened ? "Close navigation menu" : "Open navigation menu"}
                styles={{
                  root: {
                    width: 44,
                    height: 44,
                    minWidth: 44,
                    minHeight: 44,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                  },
                }}
              />
            </MediaQuery>
            <div style={{ height: "2em" }}>
              <Image
                radius="md"
                fit="contain"
                src="/captiva-logo.png"
                style={{ width: "9em", objectFit: "contain" }}
                alt="Captiva Logo"
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "end",
                alignItems: "center",
                width: "100%",
              }}
            >
              {/* <Button
                onClick={() => setAiModalOpen(true)}
                color="cap-purple"
                variant="outline"
                leftIcon={<MessageCircle size={16} />}
                style={{ marginRight: "1em", backgroundColor: "white" }}
              >
                AI Assistant
              </Button> */}
              <Button
                onClick={() => setHelpModalOpened(true)}
                color="cap-navy"
                variant="outline"
                size="xs"
                style={{ marginRight: "0.75em", backgroundColor: "white" }}
              >
                Support
              </Button>
              <Popover
                opened={popoverOpened}
                onClose={() => {
                  setPopoverOpened(false);
                  setShowDeleteAction(false);
                }}
                position="bottom"
                placement="end"
                withArrow
                target={
                  <Tooltip label="Account details" withArrow position="left">
                    <ActionIcon
                      variant="filled"
                      radius="xl"
                      size={44}
                      aria-label="User account details"
                      onClick={() => setPopoverOpened((o) => !o)}
                      sx={(theme) => ({
                        width: 44,
                        height: 44,
                        minWidth: 44,
                        minHeight: 44,
                        backgroundColor: theme.colors["cap-navy"]?.[2] || "#2C3369",
                        color: "#ffffff",
                        "&:hover": {
                          backgroundColor: theme.colors["cap-navy"]?.[4] || "#162065",
                        },
                      })}
                    >
                      <User size={24} color="#ffffff" />
                    </ActionIcon>
                  </Tooltip>
                }
                width={340}
                styles={{
                  body: {
                    padding: "16px",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    border: "1px solid #e9ecef",
                    backgroundColor: "#ffffff",
                  },
                }}
              >
                <Stack spacing="xs">
                  <Group spacing="sm" noWrap>
                    <Avatar radius="xl" color="cap-purple" size="md">
                      {auth?.claims?.email ? auth.claims.email.charAt(0).toUpperCase() : <User size={16} />}
                    </Avatar>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" weight={500} style={{ wordBreak: "break-all" }}>
                        {auth?.claims?.email || user?.email || "N/A"}
                      </Text>
                      <Text size="xs" color="dimmed">
                        {auth?.claims?.admin ? "Administrator" : "Standard User"}
                      </Text>
                    </div>
                  </Group>

                  <Divider my="xs" />

                  <Stack spacing="xs">
                    <div>
                      <Text size="xs" color="dimmed" transform="uppercase" weight={700}>
                        Company ID
                      </Text>
                      <Text size="xs" style={{ wordBreak: "break-all", fontFamily: "'Roboto Mono', monospace" }}>
                        {auth?.claims?.companyId || "N/A"}
                      </Text>
                    </div>

                    <div>
                      <Text size="xs" color="dimmed" transform="uppercase" weight={700}>
                        User UID
                      </Text>
                      <Text size="xs" style={{ wordBreak: "break-all", fontFamily: "'Roboto Mono', monospace" }}>
                        {auth?.claims?.user_id || user?.uid || "N/A"}
                      </Text>
                    </div>
                  </Stack>

                  <Divider my="xs" />

                  <Group spacing="xs" noWrap>
                    {showDeleteAction ? (
                      <Button
                        variant="outline"
                        color="red"
                        size="sm"
                        onClick={() => {
                          setPopoverOpened(false);
                          setDeleteModalOpened(true);
                        }}
                        style={{ flex: 1, minHeight: 36 }}
                      >
                        Delete account
                      </Button>
                    ) : (
                      <ActionIcon
                        variant="default"
                        size="sm"
                        aria-label="More account actions"
                        onClick={() => setShowDeleteAction(true)}
                        style={{ height: "36px", flex: "none", width: "36px" }}
                        title="More actions"
                      >
                        <Dots size={16} />
                      </ActionIcon>
                    )}
                    <Button
                      color="cap-navy"
                      size="sm"
                      onClick={async () => {
                        setPopoverOpened(false);
                        await logout();
                        navigate("/", { replace: true });
                      }}
                      style={{ flex: 1, minHeight: 36 }}
                    >
                      Logout
                    </Button>
                  </Group>
                </Stack>
              </Popover>
            </div>
          </div>
        </Header>
      }
    >
      {children}
      <Modal
        opened={deleteModalOpened}
        onClose={() => setDeleteModalOpened(false)}
        title="Are you sure?"
      >
        <Text size="sm" style={{ marginBottom: "1em" }}>
          Account deletion cannot be undone.
        </Text>
        <Group position="right" mt="md">
          <Button variant="default" color="gray" size="sm" style={{ minHeight: 36 }} onClick={() => setDeleteModalOpened(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            color="red"
            style={{ minHeight: 36 }}
            onClick={async () => {
              await deleteUser();
              setDeleteModalOpened(false);
              await logout();
              navigate("/", { replace: true });
            }}
          >
            Delete account
          </Button>
        </Group>
      </Modal>
      <RequestOnboardingHelpModal
        opened={helpModalOpened}
        onClose={() => setHelpModalOpened(false)}
        source="dashboard_help"
        defaultEmail={auth?.claims?.email || user?.email || ""}
        companyId={auth?.claims?.companyId || companyId || ""}
      />
      {/* <Modal
        opened={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        title="AI Assistant (experimental)"
        size="xl"
        styles={{
          header: {
            paddingTop: '20px',
            paddingLeft: '20px',
            paddingRight: '20px',
          },
          modal: {
            ...(isBelowLarge ? {
              width: '100vw',
              height: '90vh',
            } : {
              width: '60vw',
              maxWidth: '800px',
              height: '80vh',
            }),
            display: 'flex',
            flexDirection: 'column',
          },
          body: {
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            paddingLeft: '20px',
            paddingRight: '20px',
            paddingBottom: '20px',
          },
        }}
      >
        <ChatBox companyId={companyId} currentPath={currentPath} mode="lite" />
      </Modal> */}
    </AppShell>
  );
}

interface AppShellResponsiveProps {
  children: React.ReactNode;
  loading?: boolean;
  user: any;
  companyId: string;
  auth?: any;
}

export function AppShellResponsive({ children, loading, user, companyId, auth }: AppShellResponsiveProps) {
  return (
    <>
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', width: '100vw', position: 'fixed', top: 0, left: 0, background: 'white', zIndex: 9999 }}>
          <Loader color="#cc185f" size="lg" variant="bars" />
        </div>
      )}
      {user ? (
        <ShellWithNavigation user={user} companyId={companyId} auth={auth}>{children}</ShellWithNavigation>
      ) : (
        <>{children}</>
      )}
    </>
  );
}
